import { useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { Editor } from "@tiptap/react";
import { IconChevron } from "./icons";
import {
  IconNoteAlignCenter, IconNoteAlignJustify, IconNoteAlignLeft, IconNoteAlignRight,
  IconNoteBold, IconNoteBulletList, IconNoteClearFormat, IconNoteHighlight,
  IconNoteIndentDec, IconNoteIndentInc, IconNoteItalic, IconNoteLink, IconNoteOrderedList,
  IconNoteRedo, IconNoteSpacing, IconNoteStrike, IconNoteTextColor, IconNoteUnderline, IconNoteUndo,
} from "./noteEditor/icons";
import {
  AlignMenu, ColorMenu, FontSizeMenu, HighlightMenu, ImageMenuButton, LinkPopover, SpacingMenu,
  FONT_SIZES,
} from "./noteEditor/NoteToolbarMenus";
import { panel } from "../motion";

/**
 * Compact, Google-Docs-like formatting toolbar for the day-note editor.
 * Groups: history | font size | clear | character marks | colour/highlight |
 * link | lists | align+indent | spacing | image. Rendered on the same head
 * line as the always-visible toggle button (see DayNoteEditor). The align
 * functions are one dropdown button (matching Google Docs) rather than four
 * separate buttons.
 *
 * Dropdowns are rendered position:fixed, anchored to their trigger, because
 * the note card clips its children (overflow:hidden) and a plain absolute
 * popover would be cut off. They drop in with the app's shared `panel` motion
 * so they read like every other dropdown menu.
 */

interface NoteToolbarProps {
  editor: Editor;
  linkOpen: boolean;
  setLinkOpen: (open: boolean) => void;
}

type Menu = "fontsize" | "color" | "highlight" | "spacing" | "align" | null;

interface Anchor {
  left: number;
  top: number;
  right: number;
}

function ToolBtn({
  label, active = false, disabled = false, onClick, children,
}: { label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className={`nt-btn${active ? " is-active" : ""}`} title={label} aria-label={label}
      aria-pressed={active} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

/** Fixed-position popover card that matches the app's dropdown language. */
function ToolbarPop({
  show, anchor, align = "left", cardClassName = "", role = "menu", children,
}: {
  show: boolean;
  anchor: Anchor | null;
  align?: "left" | "right";
  cardClassName?: string;
  role?: string;
  children: ReactNode;
}) {
  return (
    <AnimatePresence>
      {show && anchor ? (
        <motion.div
          className={`nt-pop nt-pop-fixed${cardClassName ? ` ${cardClassName}` : ""}`}
          style={align === "right" ? { right: anchor.right, top: anchor.top } : { left: anchor.left, top: anchor.top }}
          variants={panel}
          initial="hidden"
          animate="show"
          exit="exit"
          role={role}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function NoteToolbar({ editor, linkOpen, setLinkOpen }: NoteToolbarProps) {
  // Re-render on every editor transaction so active states / current values
  // track the selection and content.
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const handler = () => forceUpdate();
    editor.on("transaction", handler);
    return () => {
      editor.off("transaction", handler);
    };
  }, [editor]);

  const [menu, setMenu] = useState<Menu>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const fontAnchorRef = useRef<HTMLDivElement>(null);
  const colorAnchorRef = useRef<HTMLDivElement>(null);
  const highlightAnchorRef = useRef<HTMLDivElement>(null);
  const spacingAnchorRef = useRef<HTMLDivElement>(null);
  const alignAnchorRef = useRef<HTMLDivElement>(null);
  const linkBtnRef = useRef<HTMLButtonElement>(null);
  const [linkAnchor, setLinkAnchor] = useState<Anchor | null>(null);

  const openAt = (el: HTMLDivElement | null, next: Menu) => {
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({ left: r.left, top: r.bottom + 6, right: window.innerWidth - r.right });
    setMenu(next);
  };

  const closeMenus = () => {
    setMenu(null);
    setAnchor(null);
  };

  // Position the link popover under its trigger. Runs whenever the popover
  // opens (toolbar button or Ctrl/Cmd+K — both end in linkOpen=true).
  useEffect(() => {
    if (linkOpen && linkBtnRef.current) {
      const r = linkBtnRef.current.getBoundingClientRect();
      setLinkAnchor({ left: r.left, top: r.bottom + 6, right: window.innerWidth - r.right });
    }
  }, [linkOpen]);

  // A fixed popover would stay put while the page scrolls; close on scroll.
  useEffect(() => {
    if (!menu && !linkOpen) return;
    const close = () => {
      closeMenus();
      setLinkOpen(false);
    };
    window.addEventListener("scroll", close, { passive: true });
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close);
      window.removeEventListener("resize", close);
    };
  }, [menu, linkOpen]);

  // Live font-size value in the size dropdown trigger (base text size = 16px).
  const fs = editor.getAttributes("textStyle").fontSize as string | undefined;
  const fsPx = fs ? parseInt(fs, 10) : NaN;
  const fontSizeLabel = Number.isFinite(fsPx) && FONT_SIZES.includes(fsPx) ? String(fsPx) : "16";

  // Current alignment, for the single align dropdown trigger icon.
  const activeAlign = (["left", "center", "right", "justify"] as const)
    .find((value) => editor.isActive({ textAlign: value })) ?? "left";
  const AlignIcon = activeAlign === "left" ? IconNoteAlignLeft
    : activeAlign === "right" ? IconNoteAlignRight
    : activeAlign === "justify" ? IconNoteAlignJustify
    : IconNoteAlignCenter;

  return (
    <div className="note-toolbar" role="toolbar" aria-label="Formatting">
      {menu || linkOpen ? (
        <button
          type="button"
          className="nt-backdrop"
          aria-label="Close menu"
          onClick={() => {
            closeMenus();
            setLinkOpen(false);
          }}
        />
      ) : null}

      <ToolBtn label="Undo (Ctrl+Z)" onClick={() => editor.chain().focus().undo().run()}><IconNoteUndo /></ToolBtn>
      <ToolBtn label="Redo (Ctrl+Shift+Z)" onClick={() => editor.chain().focus().redo().run()}><IconNoteRedo /></ToolBtn>

      <span className="nt-sep" />

      <div ref={fontAnchorRef} className="nt-menu-anchor">
        <ToolBtn label="Text size" active={menu === "fontsize"} onClick={() => openAt(fontAnchorRef.current, "fontsize")}>
          <span className="nt-fs-label">{fontSizeLabel}</span>
          <IconChevron />
        </ToolBtn>
      </div>
      <ToolbarPop show={menu === "fontsize"} anchor={anchor}>
        <FontSizeMenu editor={editor} onClose={closeMenus} />
      </ToolbarPop>

      <ToolBtn label="Clear formatting" onClick={() => {
        editor.chain().focus().unsetAllMarks().unsetTextAlign().clearParagraphFormat().run();
        closeMenus();
      }}><IconNoteClearFormat /></ToolBtn>

      <span className="nt-sep" />

      <ToolBtn label="Bold (Ctrl+B)" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><IconNoteBold /></ToolBtn>
      <ToolBtn label="Italic (Ctrl+I)" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><IconNoteItalic /></ToolBtn>
      <ToolBtn label="Underline (Ctrl+U)" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><IconNoteUnderline /></ToolBtn>
      <ToolBtn label="Strikethrough (Ctrl+Shift+S)" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><IconNoteStrike /></ToolBtn>

      <span className="nt-sep" />

      <div ref={colorAnchorRef} className="nt-menu-anchor">
        <ToolBtn label="Text colour" active={menu === "color"} onClick={() => openAt(colorAnchorRef.current, "color")}><IconNoteTextColor /></ToolBtn>
      </div>
      <ToolbarPop show={menu === "color"} anchor={anchor} cardClassName="nt-swatches">
        <ColorMenu editor={editor} onClose={closeMenus} />
      </ToolbarPop>

      <div ref={highlightAnchorRef} className="nt-menu-anchor">
        <ToolBtn label="Highlight colour" active={menu === "highlight"} onClick={() => openAt(highlightAnchorRef.current, "highlight")}><IconNoteHighlight /></ToolBtn>
      </div>
      <ToolbarPop show={menu === "highlight"} anchor={anchor} cardClassName="nt-swatches">
        <HighlightMenu editor={editor} onClose={closeMenus} />
      </ToolbarPop>

      <div className="nt-menu-anchor">
        <ToolBtn label="Insert link (Ctrl+K)" active={linkOpen} onClick={() => setLinkOpen(true)}>
          <span ref={linkBtnRef} className="nt-link-trigger"><IconNoteLink /></span>
        </ToolBtn>
      </div>
      <ToolbarPop show={linkOpen} anchor={linkAnchor} cardClassName="nt-pop-link" role="dialog">
        <LinkPopover editor={editor} onClose={() => setLinkOpen(false)} />
      </ToolbarPop>

      <span className="nt-sep" />

      <ToolBtn label="Bullet list (Ctrl+Shift+8)" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><IconNoteBulletList /></ToolBtn>
      <ToolBtn label="Numbered list (Ctrl+Shift+7)" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><IconNoteOrderedList /></ToolBtn>

      <span className="nt-sep" />

      <div ref={alignAnchorRef} className="nt-menu-anchor">
        <ToolBtn label="Alignment" active={menu === "align"} onClick={() => openAt(alignAnchorRef.current, "align")}>
          <AlignIcon />
          <IconChevron />
        </ToolBtn>
      </div>
      <ToolbarPop show={menu === "align"} anchor={anchor}>
        <AlignMenu editor={editor} onClose={closeMenus} />
      </ToolbarPop>
      <ToolBtn label="Increase indent (Ctrl+])" onClick={() => editor.chain().focus().indent().run()}><IconNoteIndentInc /></ToolBtn>
      <ToolBtn label="Decrease indent (Ctrl+[)" onClick={() => editor.chain().focus().outdent().run()}><IconNoteIndentDec /></ToolBtn>

      <span className="nt-sep" />

      <div ref={spacingAnchorRef} className="nt-menu-anchor">
        <ToolBtn label="Line & paragraph spacing" active={menu === "spacing"} onClick={() => openAt(spacingAnchorRef.current, "spacing")}><IconNoteSpacing /></ToolBtn>
      </div>
      <ToolbarPop show={menu === "spacing"} anchor={anchor}>
        <SpacingMenu editor={editor} onClose={closeMenus} />
      </ToolbarPop>

      <ImageMenuButton editor={editor} />
    </div>
  );
}